import { useCallback, useMemo, useState } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import { type IPassengerCounts, PassengerTypes } from "@hopper-b2b/types";
import { MobilePopoverCard, PassengerCountPicker } from "@hopper-b2b/ui";
import Person from "./person.svg?react";
import "./style.scss";

export interface IPassengerCard {
  passengerMap: { [key in PassengerTypes]?: number };
  onPassengerCountChange?: (val: { [key in PassengerTypes]?: number }) => void;
}

export const PassengerCountCard = (props: IPassengerCard) => {
  const { passengerMap, onPassengerCountChange } = props;

  const { t } = useI18nContext();

  const totalPassegnerCount = Object.values(passengerMap).reduce(
    (total, passengerValue) => {
      return (total += passengerValue);
    }
  );

  const textValues = useMemo(() => {
    return {
      passenger: totalPassegnerCount > 1 ? t("passengers") : t("passenger"),
      passengerTitles: {
        modalTitle: t("passengersCounterTitles.modalTitle"),
        adultTitle: t("passengersCounterTitles.adultTitle"),
        adultSubtitle: t("passengersCounterTitles.adultSubtitle"),
        childrenTitle: t("passengersCounterTitles.childrenTitle"),
        childrenSubtitle: t("passengersCounterTitles.childrenSubtitle"),
        infantSeatTitle: t("passengersCounterTitles.infantSeatTitle"),
        infantSeatSubtitle: t("passengersCounterTitles.infantSeatSubtitle"),
        infantLapTitle: t("passengersCounterTitles.infantLapTitle"),
        infantLapSubtitle: t("passengersCounterTitles.infantLapSubtitle"),
      },
    };
  }, [t, totalPassegnerCount]);

  const [openPassengerCountPicker, setOpenPassengerCountPicker] =
    useState(false);

  const handleClosePassengerPicker = useCallback(() => {
    setOpenPassengerCountPicker(false);
  }, []);

  const passengerMapToCount = (p: {
    [key in PassengerTypes]?: number;
  }): IPassengerCounts => {
    return {
      adultsCount: p.ADT ?? 0,
      childrenCount: p.CNN ?? 0,
      infantsInSeatCount: p.INS ?? 0,
      infantsOnLapCount: p.INF ?? 0,
    };
  };

  const passengerCountToMap = (
    p: IPassengerCounts
  ): { [key in PassengerTypes]?: number } => {
    return {
      [PassengerTypes.Adult]: p.adultsCount,
      [PassengerTypes.Child]: p.childrenCount,
      [PassengerTypes.InfantInSeat]: p.infantsInSeatCount,
      [PassengerTypes.InfantInLap]: p.infantsOnLapCount,
    };
  };

  return (
    <>
      <div className="passenger-card icon-card-container">
        <div className="icon-card-icon">
          <Person className="passenger-card-icon" alt="" />
        </div>
        <div className="icon-card-details">
          <div className="passenger-card-title">
            {totalPassegnerCount} {textValues.passenger}
          </div>
          {onPassengerCountChange ? (
            <button
              className="passenger-card-cta"
              onClick={() => setOpenPassengerCountPicker(true)}
            >
              {t("passengersCounterCta")}
            </button>
          ) : null}
        </div>
      </div>
      <MobilePopoverCard
        open={openPassengerCountPicker}
        className="checkout-popover passenger-count-picked-popover"
        onClose={handleClosePassengerPicker}
      >
        <PassengerCountPicker
          titles={textValues.passengerTitles}
          setPassengerCounts={(counts) => {
            const passengers = counts as IPassengerCounts;
            onPassengerCountChange &&
              onPassengerCountChange(passengerCountToMap(passengers));
          }}
          onClickApply={handleClosePassengerPicker}
          counts={passengerMapToCount(passengerMap)}
          className="b2b"
          onClose={handleClosePassengerPicker}
        />
      </MobilePopoverCard>
    </>
  );
};
